/**
 * 大小写转换工具
 * 用于前后端字段命名规范不一致的转换
 * 后端使用 snake_case，前端使用 camelCase
 */

/**
 * snake_case 转 camelCase
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * camelCase 转 snake_case
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * 递归转换对象的键名从 snake_case 到 camelCase
 */
export function keysToCamel<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => keysToCamel(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const camelKey = snakeToCamel(key);
        result[camelKey] = keysToCamel((obj as Record<string, unknown>)[key]);
      }
    }
    return result as T;
  }

  return obj;
}

/**
 * 递归转换对象的键名从 camelCase 到 snake_case
 */
export function keysToSnake<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => keysToSnake(item)) as T;
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const snakeKey = camelToSnake(key);
        result[snakeKey] = keysToSnake((obj as Record<string, unknown>)[key]);
      }
    }
    return result as T;
  }

  return obj;
}

/**
 * 创建axios响应转换器
 * 将后端 snake_case 响应转换为前端 camelCase
 */
export function createResponseTransformer() {
  return (data: unknown) => {
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return keysToCamel(parsed);
      } catch {
        return data;
      }
    }
    return keysToCamel(data);
  };
}

/**
 * 创建axios请求转换器
 * 将前端 camelCase 请求转换为后端 snake_case
 */
export function createRequestTransformer() {
  return (data: unknown) => {
    if (typeof data === 'string') {
      return data;
    }
    return keysToSnake(data);
  };
}
